import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import MaskedMobile from '../../common/MaskedMobile';
import TableSkeleton from '../../common/TableSkeleton';
import StatCard, { StatCardRow } from '../../common/StatCard';
import MonthYearPicker from '../../common/MonthYearPicker';
import { formatDateToDdmmyyyy } from '../../common/DateRangePicker';
import DatePicker from '../../common/DatePicker';
import Snackbar from '../../common/Snackbar';

interface PersonRow {
  assigned_to_mobile: string;
  assigned_to_name: string;
  target_value: number;
  achieved_units: number;
  direct_units: number;
  indirect_units: number;
  // Money, not units — what each leg actually paid. Sums to total_earning.
  direct_units_earned: number;
  indirect_units_earned: number;
  total_earning: number;
  // Derived, never stored — one shared target per month means there is only
  // ever one target behind this row, so its status is exactly the row's own.
  status: 'pending' | 'completed';
  achievement_pct: number | null;
  remaining_units: number;
  target_met: boolean;
  // The target behind this row — one shared target per month means a person
  // is linked to at most one, empty only if somehow none reached this row.
  target_id: string;
  start_date: string;
  end_date: string;
  // True while nobody has touched the seeded starting value.
  is_default: boolean;
  // Set by the team grouping: this person heads the group they appear in.
  is_lead?: boolean;
}

interface TeamRow {
  // Empty for the trailing group of people with no Team Lead above them.
  lead_mobile: string;
  lead_name: string;
  lead_user_id: string;
  member_count: number;
  // Includes the lead's own target, not just their executives'.
  target_value: number;
  achieved_units: number;
  direct_units: number;
  indirect_units: number;
  direct_units_earned: number;
  indirect_units_earned: number;
  total_earning: number;
  achievement_pct: number | null;
  remaining_units: number;
  target_met: boolean;
  members: PersonRow[];
}

// One entry of `targets.leads[]` — a real Lead's own row (or, for the
// leaderless bucket, just a name/mobile placeholder) plus their nested
// executives, and the team_* rollup (lead's own row + their executives,
// summed) the API computes for it. The leaderless placeholder has no target
// of its own, so every PersonRow field besides the three identity ones is
// optional here.
interface LeadEntry extends Partial<PersonRow> {
  assigned_to_mobile: string;
  assigned_to_name: string;
  assigned_to_user_id: string;
  executives: PersonRow[];
  team_target_value: number;
  team_achieved_units: number;
  team_direct_units: number;
  team_indirect_units: number;
  team_direct_units_earned: number;
  team_indirect_units_earned: number;
  team_total_earning: number;
  team_member_count: number;
  team_achievement_pct: number | null;
  team_remaining_units: number;
  team_target_met: boolean;
}

// One entry of `targets.managers[]` — Admin/SuperAdmin only. Same idea as
// LeadEntry one level up: a real Manager's own row (Marketing Manager is now
// one of DEFAULT_TARGET_ROLES, so they usually hold a personal target too —
// hence `extends Partial<PersonRow>`, present when they do) plus their Leads
// nested underneath (each already carrying its own executives and team_*
// rollup), and a manager_* rollup over all of it, own row included.
interface ManagerEntry extends Partial<PersonRow> {
  assigned_to_mobile: string;
  assigned_to_name: string;
  assigned_to_user_id: string;
  leads: LeadEntry[];
  manager_target_value: number;
  manager_achieved_units: number;
  manager_direct_units: number;
  manager_indirect_units: number;
  manager_direct_units_earned: number;
  manager_indirect_units_earned: number;
  manager_total_earning: number;
  manager_member_count: number;
  manager_achievement_pct: number | null;
  manager_remaining_units: number;
  manager_target_met: boolean;
}

interface ManagerRow {
  // Empty for the trailing group of Leads with no Manager above them.
  manager_mobile: string;
  manager_name: string;
  manager_user_id: string;
  member_count: number;
  target_value: number;
  achieved_units: number;
  direct_units: number;
  indirect_units: number;
  direct_units_earned: number;
  indirect_units_earned: number;
  total_earning: number;
  achievement_pct: number | null;
  remaining_units: number;
  target_met: boolean;
  teams: TeamRow[];
  // The Manager's own personal target row, editable the same way any
  // PersonRow is — null when this Manager holds none (or this is the
  // trailing "no Manager in scope" bucket, which isn't a real person).
  ownRow: PersonRow | null;
}

// `targets.managers[]` -> one row per Manager, each holding the same
// TeamRow[] shape teamsFromLeadEntries already builds for their Leads — so
// the Manager level is just one more layer around the exact same team
// blocks a Manager's own screen renders, not a separate rendering path.
const managersFromManagerEntries = (managers: ManagerEntry[]): ManagerRow[] => managers.map((entry) => ({
  manager_mobile: entry.assigned_to_mobile,
  // Blank for the trailing bucket (same treatment as the leaderless "team"
  // group) so the header falls back to a plain label instead of blank text.
  manager_name: entry.assigned_to_mobile ? entry.assigned_to_name : '',
  manager_user_id: entry.assigned_to_user_id,
  member_count: entry.manager_member_count,
  target_value: entry.manager_target_value,
  achieved_units: entry.manager_achieved_units,
  direct_units: entry.manager_direct_units,
  indirect_units: entry.manager_indirect_units,
  direct_units_earned: entry.manager_direct_units_earned,
  indirect_units_earned: entry.manager_indirect_units_earned,
  total_earning: entry.manager_total_earning,
  achievement_pct: entry.manager_achievement_pct,
  remaining_units: entry.manager_remaining_units,
  target_met: entry.manager_target_met,
  teams: teamsFromLeadEntries(entry.leads),
  // target_id only ever comes through when the backend spread a real own
  // row into this entry (the "no Manager in scope" placeholder never sets
  // it) — the one reliable signal that this Manager holds a personal target.
  ownRow: entry.assigned_to_mobile && entry.target_id
    ? { ...(entry as unknown as PersonRow), is_lead: false }
    : null,
}));

// `targets.leads[]` -> the same team-grouped shape the removed `by_team`
// field used to send pre-computed. Built client-side now so a team's total
// can never disagree with the per-person rows (self/lead/executives) the
// rest of the dashboard already reads off the same `targets` tree.
const teamsFromLeadEntries = (leads: LeadEntry[]): TeamRow[] => leads.map((entry) => {
  const hasOwnRow = entry.assigned_to_mobile !== '';
  const members: PersonRow[] = [
    ...(hasOwnRow ? [{ ...(entry as unknown as PersonRow), is_lead: true }] : []),
    ...entry.executives.map((e) => ({ ...e, is_lead: false })),
  ];
  return {
    lead_mobile: entry.assigned_to_mobile,
    // Blank for the leaderless bucket (same as before) so the header falls
    // back to "Reporting directly to the manager" instead of "Unassigned".
    lead_name: hasOwnRow ? entry.assigned_to_name : '',
    lead_user_id: entry.assigned_to_user_id,
    member_count: entry.team_member_count,
    target_value: entry.team_target_value,
    achieved_units: entry.team_achieved_units,
    direct_units: entry.team_direct_units,
    indirect_units: entry.team_indirect_units,
    direct_units_earned: entry.team_direct_units_earned,
    indirect_units_earned: entry.team_indirect_units_earned,
    total_earning: entry.team_total_earning,
    achievement_pct: entry.team_achievement_pct,
    remaining_units: entry.team_remaining_units,
    target_met: entry.team_target_met,
    members,
  };
});

const TABLE_HEADERS = ['S.No', 'Team Member', 'Target', 'Achieved', 'Remaining',
  'Direct Units', 'Indirect Units', 'Direct Incentive', 'Indirect Incentive', 'Progress'];

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const PencilIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const inr = (n?: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const inputCls = 'h-11 w-full px-3.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:border-[#1B4332]';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

// Local-midnight Date from a 'YYYY-MM-DD' string — `new Date(dateStr)` parses
// as UTC and can land on the previous day once rendered in a timezone behind
// UTC, which is what DatePicker's own day cells are built from instead.
const toLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Opens on the current month; the calendar reaches back ten years and one
// year forward, so a manager can set next month's targets before it starts.
const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Monthly target achievement, per person — the overseer view: Admin/SuperAdmin
 * across the org, a Marketing Manager across the team they assign targets to.
 *
 * The API returns `targets.leads[]` — the caller's own subtree reshaped as
 * one entry per Team Lead, each carrying its own row, its nested executives,
 * and a team_* rollup (the lead's own row + their executives, summed). This
 * screen groups by that same tree (see teamsFromLeadEntries) instead of
 * reading a separate pre-aggregated field, so a team's total can never
 * disagree with the per-person rows it's built from. `people` (this screen's
 * flat per-person list, used for the header totals and the shared-target
 * lookup) is just every team's members flattened back out.
 */
const TargetsOverviewTab: React.FC = () => {
  const { adminMobile, adminRole } = useAppSelector((s: RootState) => s.auth);
  const isMarketingManager = /marketing_manager|marketing manager|manager/i.test(adminRole || '');
  // The API no longer signals "you're not an overseer" by omitting a field —
  // every caller's own self+subtree comes back in `targets.leads[]` now, even
  // an Executive's (empty, since they have no subordinates). This screen is
  // still overseer-only, so the role check that used to live on the field's
  // presence now lives here instead.
  const isOverseer = /marketing_manager|marketing manager|manager|admin|superadmin/i.test(adminRole || '');
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  // A Manager's own personal target (targets.self) — outside every team, so
  // it needs its own slot. Empty for Admin/SuperAdmin (they hold no target
  // of their own; each Manager's own row is inside `managers[].ownRow`
  // instead) and for a plain Executive/Lead (this screen is overseer-only).
  const [selfRow, setSelfRow] = useState<PersonRow | null>(null);
  // Admin/SuperAdmin only — the same teams, grouped one level further under
  // each Lead's Manager. Empty for a Manager/Lead's own view, which already
  // gets exactly `teams` above with no extra wrapper.
  const [managers, setManagers] = useState<ManagerRow[]>([]);
  // Keyed by lead_mobile; absent or false means expanded, which is the default.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Keyed by manager_mobile; absent or false means expanded, which is the default.
  const [collapsedManagers, setCollapsedManagers] = useState<Record<string, boolean>>({});

  // Edit-target popup (Marketing Manager only) — edits ONE person's
  // target_value for the month, overriding the shared monthly default just
  // for them. There is no manual "add": every un-targeted member already
  // gets a default the moment this screen seeds one, so editing that default
  // (or a prior edit) is the only path — same as incentive plans, which seed
  // a default plan per month instead of requiring a manual create.
  const [editingPerson, setEditingPerson] = useState<PersonRow | null>(null);
  const [newTargetValue, setNewTargetValue] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Edit-window popup (Marketing Manager only) — edits the overall monthly
  // target's start/end date on the shared node. There is only one window per
  // month, so this changes it for every team member linked to it at once.
  const [isWindowModalOpen, setIsWindowModalOpen] = useState(false);
  const [windowFrom, setWindowFrom] = useState('');
  const [windowTo, setWindowTo] = useState('');
  const [windowSaving, setWindowSaving] = useState(false);
  const [windowError, setWindowError] = useState<string | null>(null);

  const [month, setMonth] = useState(currentMonth);

  const load = useCallback(async () => {
    if (!adminMobile) return;
    setLoading(true);
    setError(null);
    try {
      // No status filter: a month's picture is every target in it, pending
      // and completed alike — the API no longer offers one to filter by.
      const res = await marketingService.listTargets(adminMobile, { month });
      if (res?.statuscode === 200) {
        if (!isOverseer) {
          // Only overseers get this screen; anyone else lands here by URL.
          // (Every role's own self+subtree still comes back in
          // targets.leads[] — this is a UI gate, not an API one.)
          setTeams([]);
          setManagers([]);
          setPeople([]);
          setSelfRow(null);
          setError('This view is available to a Marketing Manager, Admin or SuperAdmin.');
          return;
        }
        // Admin/SuperAdmin get `targets.managers[]` instead of `targets.leads[]`
        // — the same Lead entries, grouped one level further under each Lead's
        // Manager. Either way, flattening back down to teams/people keeps the
        // header totals and the shared-window lookup working unchanged.
        const managerEntries: ManagerEntry[] | undefined = res.targets?.managers;
        const managerRows = managerEntries ? managersFromManagerEntries(managerEntries) : [];
        const teamRows = managerEntries
          ? managerRows.flatMap((m) => m.teams)
          : teamsFromLeadEntries(res.targets?.leads || []);
        setManagers(managerRows);
        setTeams(teamRows);
        // A Manager's own personal target (targets.self) sits outside every
        // team — null for Admin/SuperAdmin (they hold no target of their own;
        // targets.self is only ever non-null for a real Manager/Lead caller).
        const ownSelfRow: PersonRow | null = res.targets?.self || null;
        setSelfRow(ownSelfRow);
        // Every person on screen is already inside some team's members (the
        // lead's own row included), so flattening that gives the same flat
        // list the old by_person/by_team did — plus each Manager's own row
        // (ownRow) and the caller's own row (selfRow), both of which sit
        // outside every team and would otherwise be missing from the header
        // totals/shared-window lookup below.
        const managerOwnRows = managerRows.flatMap((m) => (m.ownRow ? [m.ownRow] : []));
        setPeople([
          ...teamRows.flatMap((t) => t.members),
          ...managerOwnRows,
          ...(ownSelfRow ? [ownSelfRow] : []),
        ]);
      } else {
        setError(res?.detail || res?.message || 'Failed to load targets');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, month, isOverseer]);

  useEffect(() => { load(); }, [load]);

  // The overall monthly target's window — one shared node per month, so
  // every person's row carries the same start_date/end_date; the first
  // person on screen is as good a source as any.
  const overallWindow = useMemo(() => (
    people[0] ? { start: people[0].start_date, end: people[0].end_date } : null
  ), [people]);

  const openEditWindowModal = () => {
    if (!overallWindow) return;
    setWindowFrom(overallWindow.start);
    setWindowTo(overallWindow.end);
    setWindowError(null);
    setIsWindowModalOpen(true);
  };

  const handleSubmitWindowModal = async () => {
    if (!windowFrom) {
      setWindowError('Please select Start Date');
      return;
    }
    if (!windowTo) {
      setWindowError('Please select End Date');
      return;
    }
    if (windowFrom > windowTo) {
      setWindowError('End Date cannot be earlier than Start Date');
      return;
    }
    setWindowSaving(true);
    setWindowError(null);
    const res = await marketingService.updateTargetWindow(adminMobile!, {
      start_date: windowFrom, end_date: windowTo, month,
    });
    setWindowSaving(false);
    if (res?.error || res?.statuscode !== 200) {
      setWindowError(res?.error || res?.detail || res?.message || 'Could not update the target window');
      return;
    }
    setIsWindowModalOpen(false);
    setSnackbar({ message: `Target window updated — ${res.people_affected ?? 'every'} team member${res.people_affected === 1 ? '' : 's'} affected`, type: 'success' });
    await load();
  };

  const openEditPersonTargetModal = (p: PersonRow) => {
    setEditingPerson(p);
    setNewTargetValue(String(p.target_value));
    setAddError(null);
  };

  const handleSubmitTargetModal = async () => {
    if (!editingPerson) return;
    const value = Number(newTargetValue);
    if (!Number.isInteger(value) || value < 1) {
      setAddError('Target must be a whole number of 1 or more');
      return;
    }
    setAddSaving(true);
    setAddError(null);
    const res = await marketingService.updatePersonTarget(adminMobile!, editingPerson.assigned_to_mobile, {
      target_value: value, month,
    });
    setAddSaving(false);
    if (res?.error || res?.statuscode !== 200) {
      setAddError(res?.error || res?.detail || res?.message || 'Could not update the target');
      return;
    }
    setEditingPerson(null);
    setSnackbar({ message: `${editingPerson.assigned_to_name || 'Target'} updated to ${value} units`, type: 'success' });
    // Reload rather than patch state locally: achievement %, remaining and the
    // header totals are all computed server-side.
    await load();
  };

  const totals = useMemo(() => people.reduce((acc, p) => {
    const direct = p.direct_units_earned || 0;
    const indirect = p.indirect_units_earned || 0;
    return {
      assigned: acc.assigned + p.target_value,
      achieved: acc.achieved + p.achieved_units,
      // Sum of the two legs shown right below it, not the API's own
      // total_earning -- keeps the header card always consistent with its
      // own "Direct · Indirect" subtitle even if that field ever disagrees.
      earning: acc.earning + direct + indirect,
      directEarning: acc.directEarning + direct,
      indirectEarning: acc.indirectEarning + indirect,
    };
  }, { assigned: 0, achieved: 0, earning: 0, directEarning: 0, indirectEarning: 0 }), [people]);

  const bar = (pct: number | null) => {
    const width = Math.min(100, Math.max(0, pct ?? 0));
    const tone = pct === null ? 'bg-slate-300'
      : pct >= 100 ? 'bg-emerald-500'
      : pct >= 50 ? 'bg-amber-500' : 'bg-rose-400';
    return (
      <div className="w-28 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${width}%` }} />
      </div>
    );
  };

  // One person's row. Shared by the grouped and flat layouts so the two can
  // never drift. Each person's target defaults from the shared monthly
  // value until a Manager/Lead overrides it just for them (the pencil icon).
  const renderRow = (p: PersonRow, i: number) => (
                  <tr key={p.assigned_to_mobile} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-4 text-slate-500 font-medium text-[13px] whitespace-nowrap">{i + 1}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800 text-[13px] whitespace-nowrap">{p.assigned_to_name || '—'}</span>
                        {/* Marks who heads the group, so the first row is not
                            read as just another member. */}
                        {p.is_lead && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-[#1B4332] bg-[#1B4332]/10 rounded-md px-1.5 py-0.5">
                            lead
                          </span>
                        )}
                      </div>
                      <MaskedMobile mobile={p.assigned_to_mobile} className="text-slate-500 font-medium text-[12px]" />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[13px] text-slate-700">{p.target_value}</span>
                        {p.is_default && (
                          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400 bg-slate-100 rounded-md px-1.5 py-0.5">
                            default
                          </span>
                        )}
                        {isMarketingManager && (
                          <button
                            type="button"
                            onClick={() => openEditPersonTargetModal(p)}
                            title="Edit this person's target"
                            className="text-slate-400 hover:text-[#1B4332] transition-colors cursor-pointer bg-transparent border-none p-0.5"
                          >
                            <PencilIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-emerald-700 font-black text-[13px]">{p.achieved_units}</td>
                    <td className="px-4 py-4 text-slate-600 font-medium text-[13px]">
                      {p.achievement_pct === null ? '—' : p.remaining_units}
                    </td>
                    <td className="px-4 py-4 text-indigo-700 font-medium text-[13px]">{p.direct_units}</td>
                    <td className="px-4 py-4 text-purple-700 font-medium text-[13px]">{p.indirect_units}</td>
                    <td className="px-4 py-4 text-amber-700 font-semibold text-[13px] whitespace-nowrap">{inr(p.direct_units_earned)}</td>
                    <td className="px-4 py-4 text-amber-700 font-semibold text-[13px] whitespace-nowrap">{inr(p.indirect_units_earned)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {bar(p.achievement_pct)}
                        {/* Null means no target was set — distinct from 0%, which
                            would blame someone for a target nobody gave them. */}
                        <span className={`text-[12px] font-black ${
                          p.achievement_pct === null ? 'text-slate-400'
                            : p.target_met ? 'text-emerald-700' : 'text-slate-600'}`}>
                          {p.achievement_pct === null ? 'no target' : `${p.achievement_pct}%`}
                        </span>
                      </div>
                    </td>
                  </tr>
  );

  // A plain table of full rows (same headers/columns/edit-pencil as every
  // Lead/Executive row) around whichever PersonRow list is given — used for
  // a Manager's own target (a single row, not a team) so it shows with
  // exactly the same detail a Lead's or Executive's row does, not a
  // stripped-down summary.
  const renderPersonTable = (rows: PersonRow[]) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-100">
            {TABLE_HEADERS.map((h) => (
              <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((p, i) => renderRow(p, i))}
        </tbody>
      </table>
    </div>
  );

  // One team's collapsible card (its Lead/direct-reports header + member
  // table). Shared by the plain teams.map(...) case (a Manager/Lead's own
  // screen) and the Manager-grouped case (Admin/SuperAdmin) below, so the
  // two can never render a team differently.
  const renderTeamBlock = (t: TeamRow) => {
    const key = t.lead_mobile || '__direct__';
    const isOpen = collapsed[key] !== true;
    return (
      <div key={key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setCollapsed((c) => ({ ...c, [key]: isOpen }))}
          className="w-full flex flex-wrap items-center gap-3 px-4 py-3.5 bg-slate-50/70 hover:bg-slate-100/70 transition-colors cursor-pointer text-left"
        >
          <ChevronIcon className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          <span className="font-bold text-slate-800 text-[13px] truncate">
            {/* An empty lead_mobile is the group for people with no
                Team Lead above them — named, not left blank. */}
            {t.lead_name ? `${t.lead_name}'s Team` : 'Reporting directly to the manager'}
          </span>
          <span className="text-[11px] font-semibold text-slate-400">
            {t.member_count} member{t.member_count === 1 ? '' : 's'}
          </span>
          <div className="ml-auto flex items-center gap-4">
            <span className="text-[12px] font-semibold text-slate-500 whitespace-nowrap">
              {t.target_value} assigned
            </span>
            <span className="text-[12px] font-bold text-emerald-700 whitespace-nowrap">
              {t.achieved_units} achieved
            </span>
            <div className="flex items-center gap-2">
              {bar(t.achievement_pct)}
              <span className={`text-[12px] font-black ${
                t.achievement_pct === null ? 'text-slate-400'
                  : t.target_met ? 'text-emerald-700' : 'text-slate-600'}`}>
                {t.achievement_pct === null ? 'no target' : `${t.achievement_pct}%`}
              </span>
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  {TABLE_HEADERS.map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {t.members.map((m, i) => renderRow(m, i))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* `month` also restricts the API to period='monthly', which is what
            this screen is about — weekly and daily targets are excluded. */}
        <MonthYearPicker
          value={month}
          onChange={setMonth}
          accentColor="#10b981"
          allowFuture
        />
        <button
          onClick={() => load()}
          disabled={loading}
          title="Refresh"
          className={`flex items-center justify-center gap-2 shrink-0 h-[42px] px-4 text-black border border-slate-200 shadow-sm rounded-xl bg-white outline-none hover:bg-slate-50 hover:ring-2 hover:ring-[#1B4332]/20 transition-all duration-200 cursor-pointer text-sm font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* A Manager's own personal target (targets.self) — outside every
          team/group below, so it needs its own section at the top instead of
          being buried inside a team table or silently missing. Shown as a
          full row (same columns, same edit pencil) as any Lead/Executive
          row gets — not a stripped-down summary. */}
      {selfRow && (
        <div className="space-y-2">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1">Your Target</div>
          {renderPersonTable([selfRow])}
        </div>
      )}

      {/* The overall monthly target's window — one shared node per month, so
          editing it here changes it for every team member linked to it. */}
      {overallWindow && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-2">
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Start Date</div>
            <div className="text-[15px] font-bold text-slate-800">{formatDateToDdmmyyyy(overallWindow.start)}</div>
          </div>
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">End Date</div>
            <div className="text-[15px] font-bold text-slate-800">{formatDateToDdmmyyyy(overallWindow.end)}</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {isMarketingManager && (
              <button
                type="button"
                onClick={openEditWindowModal}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer"
              >
                <PencilIcon className="w-3.5 h-3.5" />
                Edit Dates
              </button>
            )}
          </div>
        </div>
      )}

      {people.length > 0 && (
        <StatCardRow className="mb-0">
          <StatCard title="Assigned" value={`${totals.assigned} units`} />
          <StatCard title="Achieved" value={`${totals.achieved} units`} valueClassName="text-emerald-700" />
          {/* Which leg the money came from — the same split the rows show. */}
          <StatCard
            title="Incentive Earned"
            value={inr(totals.earning)}
            valueClassName="text-amber-700"
            subtitle={`Direct ${inr(totals.directEarning)} · Indirect ${inr(totals.indirectEarning)}`}
          />
        </StatCardRow>
      )}

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100 bg-slate-50/60">
                {TABLE_HEADERS.map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableSkeleton rows={8} cols={10} />
            </tbody>
          </table>
        </div>
      )}
      {!loading && error && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-rose-500 font-medium">
          {error}
        </div>
      )}
      {!loading && !error && people.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-slate-400 font-medium">
          No monthly targets for this month.
        </div>
      )}

      {!loading && !error && people.length > 0 && (
        managers.length > 0 ? (
          /* Admin/SuperAdmin: one level further than a Manager's own screen —
             every Manager in scope, each with their own Leads' team blocks
             (renderTeamBlock) nested underneath, so a Manager's rollup can
             never disagree with the same team totals their own screen shows. */
          <div className="space-y-4">
            {managers.map((mgr) => {
              const key = mgr.manager_mobile || '__unassigned__';
              const isOpen = collapsedManagers[key] !== true;
              return (
                <div key={key} className="bg-slate-50 rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCollapsedManagers((c) => ({ ...c, [key]: isOpen }))}
                    className="w-full flex flex-wrap items-center gap-3 px-4 py-3.5 bg-slate-100/80 hover:bg-slate-200/60 transition-colors cursor-pointer text-left"
                  >
                    <ChevronIcon className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    <span className="font-black text-slate-900 text-[14px] truncate">
                      {/* An empty manager_mobile is every Lead this walk
                          couldn't attach to a real Manager in scope. */}
                      {mgr.manager_name ? `${mgr.manager_name}'s Org` : 'No Manager Assigned'}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500">
                      {mgr.member_count} member{mgr.member_count === 1 ? '' : 's'}
                    </span>
                    <div className="ml-auto flex items-center gap-4">
                      <span className="text-[12px] font-semibold text-slate-600 whitespace-nowrap">
                        {mgr.target_value} assigned
                      </span>
                      <span className="text-[12px] font-bold text-emerald-700 whitespace-nowrap">
                        {mgr.achieved_units} achieved
                      </span>
                      <div className="flex items-center gap-2">
                        {bar(mgr.achievement_pct)}
                        <span className={`text-[12px] font-black ${
                          mgr.achievement_pct === null ? 'text-slate-400'
                            : mgr.target_met ? 'text-emerald-700' : 'text-slate-600'}`}>
                          {mgr.achievement_pct === null ? 'no target' : `${mgr.achievement_pct}%`}
                        </span>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="p-3 space-y-3">
                      {/* The Manager's own personal target — not one of their
                          Leads' teams, so it isn't inside any renderTeamBlock.
                          Shown as a full row, same columns/edit-pencil as any
                          Lead/Executive row, not a stripped-down summary. */}
                      {mgr.ownRow && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Own Target</div>
                          {renderPersonTable([mgr.ownRow])}
                        </div>
                      )}
                      {mgr.teams.map((t) => renderTeamBlock(t))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : teams.length > 0 ? (
          /* Grouped under the Team Lead each person reports to. The lead's own
             target sits inside their team's total, so the team figures add up
             to the header cards above. */
          <div className="space-y-4">
            {teams.map((t) => renderTeamBlock(t))}
          </div>
        ) : (
          /* No grouping came back (an older API, or a caller the rollup does
             not cover) — the flat list still works. */
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  {TABLE_HEADERS.map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {people.map((p, i) => renderRow(p, i))}
              </tbody>
            </table>
          </div>
        )
      )}

      {editingPerson && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all animate-scaleIn border border-white/20 max-h-[92vh] flex flex-col">
            <div className="bg-gradient-to-r from-[#1B4332] to-[#14331f] p-4 flex items-center justify-between text-white shrink-0">
              <h3 className="text-base font-bold">Edit Target</h3>
              <button onClick={() => setEditingPerson(null)} className="p-1.5 hover:bg-white/10 rounded-lg transition-all cursor-pointer">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto bg-white p-5 space-y-6">
              <div className="text-[13px] text-slate-500 font-medium">
                Sets {editingPerson.assigned_to_name || 'this person'}'s own target for this month — everyone else's target is unaffected.
              </div>

              <div>
                <label className={labelCls}>Target Count</label>
                <input
                  type="number"
                  min={1}
                  value={newTargetValue}
                  onChange={(e) => setNewTargetValue(e.target.value)}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  onKeyDown={(e) => ['ArrowUp', 'ArrowDown', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                  placeholder="e.g. 20"
                  className={inputCls}
                />
              </div>

              {addError && (
                <div className="bg-rose-50 border border-rose-100 rounded-xl px-4 py-3 text-sm text-rose-700 font-medium">
                  {addError}
                </div>
              )}
            </div>

            <div className="shrink-0 flex gap-3 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setEditingPerson(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitTargetModal}
                disabled={addSaving}
                className="flex-[1.5] px-4 py-2.5 bg-[#1B4332] hover:bg-[#14331f] text-white rounded-lg font-semibold text-sm shadow-sm transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              >
                {addSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isWindowModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all animate-scaleIn border border-white/20 max-h-[92vh] flex flex-col">
            <div className="bg-gradient-to-r from-[#1B4332] to-[#14331f] p-4 flex items-center justify-between text-white shrink-0">
              <h3 className="text-base font-bold">Edit Target Dates</h3>
              <button onClick={() => setIsWindowModalOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-all cursor-pointer">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto bg-white p-5 space-y-6">
              <div className="text-[13px] text-slate-500 font-medium">
                Changes the target window for every Marketing Lead/Executive who currently shares it.
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Start Date</label>
                  <DatePicker
                    value={windowFrom}
                    onChange={setWindowFrom}
                    allowFutureDates
                    accentColor="#1B4332"
                    buttonClassName="w-full h-11 rounded-lg"
                  />
                </div>
                <div>
                  <label className={labelCls}>End Date</label>
                  <DatePicker
                    value={windowTo}
                    onChange={setWindowTo}
                    allowFutureDates
                    minDate={windowFrom ? toLocalDate(windowFrom) : undefined}
                    accentColor="#1B4332"
                    buttonClassName="w-full h-11 rounded-lg"
                  />
                </div>
              </div>

              {windowError && (
                <div className="bg-rose-50 border border-rose-100 rounded-xl px-4 py-3 text-sm text-rose-700 font-medium">
                  {windowError}
                </div>
              )}
            </div>

            <div className="shrink-0 flex gap-3 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsWindowModalOpen(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitWindowModal}
                disabled={windowSaving}
                className="flex-[1.5] px-4 py-2.5 bg-[#1B4332] hover:bg-[#14331f] text-white rounded-lg font-semibold text-sm shadow-sm transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              >
                {windowSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {snackbar && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />
      )}
    </div>
  );
};

export default TargetsOverviewTab;
